import React, { useState, createElement } from 'react';
import { TrendingUpIcon, PackageIcon, AlertTriangleIcon, CheckCircleIcon, ClockIcon, DownloadIcon, FilterIcon, SearchIcon, UserIcon, CalendarIcon } from 'lucide-react';
interface QuoteItem {
  design: string;
  quantity: number;
}
interface Quote {
  id: string;
  quoteNumber: string;
  customerName: string;
  items: QuoteItem[];
  probability: number;
  expectedDelivery: string;
  totalMachines: number;
  createdDate: string;
  status: 'active' | 'pending' | 'negotiation';
}
const mockQuotes: Quote[] = [{
  id: '1',
  quoteNumber: 'QT-2024-001',
  customerName: 'Green Valley Farms',
  items: [{
    design: '6" 5/8 GALV',
    quantity: 15
  }, {
    design: '8" 5/8 GALV',
    quantity: 8
  }],
  probability: 85,
  expectedDelivery: '2024-03-15',
  totalMachines: 23,
  createdDate: '2024-01-10',
  status: 'negotiation'
}, {
  id: '2',
  quoteNumber: 'QT-2024-002',
  customerName: 'Sunrise Agriculture Co.',
  items: [{
    design: '5" GALV',
    quantity: 12
  }, {
    design: 'High Profile',
    quantity: 5
  }],
  probability: 70,
  expectedDelivery: '2024-03-20',
  totalMachines: 17,
  createdDate: '2024-01-12',
  status: 'active'
}, {
  id: '3',
  quoteNumber: 'QT-2024-003',
  customerName: 'Midwest Irrigation Systems',
  items: [{
    design: '6" 5/8 GALV',
    quantity: 25
  }, {
    design: 'Linear',
    quantity: 10
  }],
  probability: 60,
  expectedDelivery: '2024-04-01',
  totalMachines: 35,
  createdDate: '2024-01-15',
  status: 'active'
}, {
  id: '4',
  quoteNumber: 'QT-2024-004',
  customerName: 'Desert Bloom Farms',
  items: [{
    design: '8" 5/8 GALV',
    quantity: 18
  }, {
    design: '6" 5/8 POLY',
    quantity: 12
  }],
  probability: 45,
  expectedDelivery: '2024-04-10',
  totalMachines: 30,
  createdDate: '2024-01-18',
  status: 'pending'
}, {
  id: '5',
  quoteNumber: 'QT-2024-005',
  customerName: 'Golden Harvest Ltd.',
  items: [{
    design: '5" GALV',
    quantity: 8
  }, {
    design: '6" 5/8 GALV',
    quantity: 10
  }],
  probability: 90,
  expectedDelivery: '2024-03-25',
  totalMachines: 18,
  createdDate: '2024-01-20',
  status: 'negotiation'
}];
const currentStock = {
  '6" 5/8 GALV': 65,
  '8" 5/8 GALV': 8,
  '5" GALV': 2,
  'High Profile': 22,
  Linear: 1,
  '6" 5/8 POLY': 5
};
export function StockForecastPanel() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProbability, setSelectedProbability] = useState('all');
  const calculateForecast = () => {
    const forecast: Record<string, {
      expected: number;
      weighted: number;
      quotes: number;
    }> = {};
    mockQuotes.forEach(quote => {
      quote.items.forEach(item => {
        if (!forecast[item.design]) {
          forecast[item.design] = {
            expected: 0,
            weighted: 0,
            quotes: 0
          };
        }
        forecast[item.design].expected += item.quantity;
        forecast[item.design].weighted += item.quantity * (quote.probability / 100);
        forecast[item.design].quotes += 1;
      });
    });
    return forecast;
  };
  const forecast = calculateForecast();
  const totalQuotes = mockQuotes.length;
  const totalExpectedDemand = Object.values(forecast).reduce((sum, item) => sum + item.weighted, 0);
  const highProbabilityQuotes = mockQuotes.filter(q => q.probability >= 70).length;
  const getProbabilityColor = (probability: number) => {
    if (probability >= 70) return 'bg-green-100 text-green-700 border-green-200';
    if (probability >= 50) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-orange-100 text-orange-700 border-orange-200';
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'negotiation':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pending':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const handleDownloadExcel = () => {
    const headers = ['Quote Number', 'Customer', 'Design', 'Quantity', 'Probability %', 'Expected Delivery', 'Status'];
    const rows: string[][] = [];
    mockQuotes.forEach(quote => {
      quote.items.forEach(item => {
        rows.push([quote.quoteNumber, quote.customerName, item.design, item.quantity.toString(), `${quote.probability}%`, quote.expectedDelivery, quote.status.toUpperCase()]);
      });
    });
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-forecast-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  return <div className="space-y-6" data-id="element-362">
      <div className="flex items-center justify-between" data-id="element-363">
        <div data-id="element-364">
          <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-365">
            Stock Forecast
          </h2>
          <p className="text-gray-600 text-sm" data-id="element-366">
            Predicted demand based on active quotes and probabilities
          </p>
        </div>
        <button onClick={handleDownloadExcel} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/30" data-id="element-367">
          <DownloadIcon className="w-4 h-4" data-id="element-368" />
          Export Forecast
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-369">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-5 text-white shadow-lg shadow-blue-500/30" data-id="element-370">
          <div className="flex items-center justify-between mb-3" data-id="element-371">
            <PackageIcon className="w-6 h-6 opacity-70" data-id="element-372" />
          </div>
          <p className="text-sm opacity-80 mb-1" data-id="element-373">Active Quotes</p>
          <p className="text-3xl font-bold" data-id="element-374">{totalQuotes}</p>
        </div>
        <div className="bg-white rounded-lg p-5 border border-gray-200" data-id="element-375">
          <div className="flex items-center justify-between mb-3" data-id="element-376">
            <TrendingUpIcon className="w-6 h-6 text-blue-500" data-id="element-377" />
          </div>
          <p className="text-sm text-gray-600 mb-1" data-id="element-378">Expected Demand</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-379">
            {Math.round(totalExpectedDemand)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-380">weighted units</p>
        </div>
        <div className="bg-white rounded-lg p-5 border border-gray-200" data-id="element-381">
          <div className="flex items-center justify-between mb-3" data-id="element-382">
            <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-383" />
          </div>
          <p className="text-sm text-gray-600 mb-1" data-id="element-384">High Probability</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-385">
            {highProbabilityQuotes}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-386">≥70% quotes</p>
        </div>
        <div className="bg-white rounded-lg p-5 border border-gray-200" data-id="element-387">
          <div className="flex items-center justify-between mb-3" data-id="element-388">
            <AlertTriangleIcon className="w-6 h-6 text-orange-500" data-id="element-389" />
          </div>
          <p className="text-sm text-gray-600 mb-1" data-id="element-390">At Risk Designs</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-391">
            {Object.entries(forecast).filter(([design, data]) => currentStock[design as keyof typeof currentStock] < data.weighted).length}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-392">insufficient stock</p>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-393">
        <h3 className="text-lg font-bold text-gray-900 mb-5" data-id="element-394">
          Forecast by Design
        </h3>
        <div className="space-y-4" data-id="element-395">
          {Object.entries(forecast).map(([design, data]) => {
          const current = currentStock[design as keyof typeof currentStock] || 0;
          const shortage = Math.max(0, Math.ceil(data.weighted) - current);
          const isAtRisk = shortage > 0;
          return <div key={design} className={`rounded-lg p-5 border ${isAtRisk ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-396">
                <div className="flex items-start justify-between mb-4" data-id="element-397">
                  <div className="flex items-start gap-4 flex-1" data-id="element-398">
                    <div className={`w-12 h-12 bg-gradient-to-br ${isAtRisk ? 'from-red-500 to-red-600 shadow-red-500/30' : 'from-blue-500 to-blue-600 shadow-blue-500/30'} rounded-lg flex items-center justify-center shadow-lg`} data-id="element-399">
                      <PackageIcon className="w-6 h-6 text-white" data-id="element-400" />
                    </div>
                    <div className="flex-1" data-id="element-401">
                      <h4 className="text-lg font-bold text-gray-900 mb-3" data-id="element-402">
                        {design}
                      </h4>
                      <div className="grid grid-cols-5 gap-4" data-id="element-403">
                        <div data-id="element-404">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-405">
                            Current Stock
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-406">
                            {current}
                          </p>
                        </div>
                        <div data-id="element-407">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-408">
                            Expected Demand
                          </p>
                          <p className="text-lg font-bold text-blue-600" data-id="element-409">
                            {Math.round(data.weighted)}
                          </p>
                        </div>
                        <div data-id="element-410">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-411">
                            Max Demand
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-412">
                            {data.expected}
                          </p>
                        </div>
                        <div data-id="element-413">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-414">
                            Active Quotes
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-415">
                            {data.quotes}
                          </p>
                        </div>
                        <div data-id="element-416">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-417">
                            {isAtRisk ? 'Shortage' : 'Coverage'}
                          </p>
                          <p className={`text-lg font-bold ${isAtRisk ? 'text-red-600' : 'text-green-600'}`} data-id="element-418">
                            {isAtRisk ? shortage : '+' + (current - Math.ceil(data.weighted))}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {isAtRisk && <div className="bg-red-100 rounded-lg p-3 border border-red-200" data-id="element-419">
                    <div className="flex items-center gap-2" data-id="element-420">
                      <AlertTriangleIcon className="w-4 h-4 text-red-600" data-id="element-421" />
                      <p className="text-sm text-red-700 font-medium" data-id="element-422">
                        Insufficient stock to cover expected demand. Order{' '}
                        {shortage} units recommended.
                      </p>
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-423">
        <div className="p-6 border-b border-gray-200" data-id="element-424">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-425">
            Active Quotes
          </h3>
          <div className="flex items-center gap-4" data-id="element-426">
            <div className="flex-1 relative" data-id="element-427">
              <SearchIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-428" />
              <input type="text" placeholder="Search by customer or quote number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white text-gray-900 pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all" data-id="element-429" />
            </div>
            <select value={selectedProbability} onChange={e => setSelectedProbability(e.target.value)} className="px-4 py-3 bg-white text-gray-700 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all" data-id="element-430">
              <option value="all" data-id="element-431">All Probabilities</option>
              <option value="high" data-id="element-432">High (≥70%)</option>
              <option value="medium" data-id="element-433">Medium (50-69%)</option>
              <option value="low" data-id="element-434">Low (&lt;50%)</option>
            </select>
            <button className="flex items-center gap-2 px-5 py-3 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-435">
              <FilterIcon className="w-5 h-5" data-id="element-436" />
              Filters
            </button>
          </div>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-437">
          {mockQuotes.map(quote => <div key={quote.id} className="p-6 hover:bg-gray-50 transition-colors" data-id="element-438">
              <div className="flex items-start justify-between mb-4" data-id="element-439">
                <div className="flex items-start gap-4 flex-1" data-id="element-440">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/30" data-id="element-441">
                    <UserIcon className="w-6 h-6 text-white" data-id="element-442" />
                  </div>
                  <div className="flex-1" data-id="element-443">
                    <div className="flex items-center gap-3 mb-2" data-id="element-444">
                      <h4 className="text-lg font-bold text-gray-900" data-id="element-445">
                        {quote.quoteNumber}
                      </h4>
                      <span className={`px-2.5 py-1 rounded text-xs font-semibold border ${getProbabilityColor(quote.probability)}`} data-id="element-446">
                        {quote.probability}% probability
                      </span>
                      <span className={`px-2.5 py-1 rounded text-xs font-semibold border ${getStatusColor(quote.status)}`} data-id="element-447">
                        {quote.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-448">
                      {quote.customerName}
                    </p>
                    <div className="flex items-center gap-6 text-sm text-gray-600 mb-4" data-id="element-449">
                      <div className="flex items-center gap-2" data-id="element-450">
                        <CalendarIcon className="w-4 h-4" data-id="element-451" />
                        <span data-id="element-452">Expected: {quote.expectedDelivery}</span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-453">
                        <PackageIcon className="w-4 h-4" data-id="element-454" />
                        <span data-id="element-455">{quote.totalMachines} machines</span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-456">
                        <ClockIcon className="w-4 h-4" data-id="element-457" />
                        <span data-id="element-458">Created: {quote.createdDate}</span>
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-459">
                      {quote.items.map((item, idx) => <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-460">
                          <div className="flex items-center gap-3" data-id="element-461">
                            <PackageIcon className="w-4 h-4 text-gray-400" data-id="element-462" />
                            <span className="text-sm font-medium text-gray-900" data-id="element-463">
                              {item.design}
                            </span>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-464">
                            <span className="text-sm text-gray-600" data-id="element-465">
                              Qty: {item.quantity}
                            </span>
                            <span className="text-sm font-semibold text-blue-600" data-id="element-466">
                              Weighted:{' '}
                              {Math.round(item.quantity * (quote.probability / 100))}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}